import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  number,
  arrayOf,
  string,
  oneOfType,
  shape,
  func,
  bool,
  node,
} from 'prop-types';
import {
  Toolbar,
  Typography,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import HeightIcon from '@material-ui/icons/Height';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import styled from 'styled-components/macro';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

// ---- Styled Components ----

const StyledToolbar = styled(Toolbar)`
  width: 100%;
  display: flex;
  justify-content: space-between;
  &:focus {
    outline: 0;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  height: ${props => props.height || 600}px;
  position: relative;
  background: #fff;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  table-layout: fixed;
  font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fafafa;
`;

const Th = styled.th`
  position: relative;
  padding: 0 12px;
  height: 50px;
  text-align: left;
  font-weight: 500;
  font-size: 13px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-bottom: 1px solid #e0e0e0;
  user-select: none;
  box-sizing: border-box;

  &.drag-over {
    background: rgba(25, 118, 210, 0.06);
    box-shadow: inset 2px 0 0 #1976d2;
  }

  ${props =>
    props.pinned === 'left' &&
    `
    position: sticky;
    left: ${props.pinnedOffset || 0}px;
    z-index: 3;
    background: #fafafa;
    box-shadow: 2px 0 4px rgba(0,0,0,0.06);
  `}

  ${props =>
    props.pinned === 'right' &&
    `
    position: sticky;
    right: ${props.pinnedOffset || 0}px;
    z-index: 3;
    background: #fafafa;
    box-shadow: -2px 0 4px rgba(0,0,0,0.06);
  `}
`;

const ThContent = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  gap: 4px;
  cursor: pointer;
`;

const ThLabel = styled.span`
  flex: 1;
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  cursor: pointer;
  white-space: nowrap;
  min-width: 0;
`;

const ThLabelText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  color: #bbb;
  cursor: grab;
  flex-shrink: 0;
  margin-right: 2px;

  &:active {
    cursor: grabbing;
  }
`;

const SortIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 2px;
  color: ${props => (props.active ? '#333' : '#bbb')};
  transition: color 0.15s;
  flex-shrink: 0;
`;

const ResizeHandle = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 8px;
  cursor: col-resize;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.35;
  transition: opacity 0.15s;

  &::after {
    content: '';
    width: 2px;
    height: 16px;
    border-left: 1px solid #bbb;
    border-right: 1px solid #bbb;
  }

  &:hover {
    opacity: 1;
  }

  ${props =>
    props.isResizing &&
    `
    opacity: 1;
    &::after {
      border-color: #1976d2;
    }
  `}
`;

const MenuBtn = styled(IconButton)`
  && {
    padding: 2px;
    opacity: 0.35;
    transition: opacity 0.15s;
    &:hover {
      opacity: 1;
    }
  }
`;

const Tr = styled.tr`
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  &:hover {
    background: #f5f5f5;
  }
  ${props => props.selected && 'background: #e3f2fd !important;'}
`;

const CellClamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
`;

const Td = styled.td`
  padding: 0 12px;
  height: 56px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-bottom: 1px solid #f0f0f0;
  box-sizing: border-box;

  ${props =>
    props.pinned === 'left' &&
    `
    position: sticky;
    left: ${props.pinnedOffset || 0}px;
    z-index: 1;
    background: #fff;
    box-shadow: 2px 0 4px rgba(0,0,0,0.06);
  `}

  ${props =>
    props.pinned === 'right' &&
    `
    position: sticky;
    right: ${props.pinnedOffset || 0}px;
    z-index: 1;
    background: #fff;
    box-shadow: -2px 0 4px rgba(0,0,0,0.06);
  `}
`;

const CheckboxTh = styled(Th)`
  width: 50px;
  min-width: 50px;
  max-width: 50px;
  padding: 0 8px;
  overflow: visible;
  text-overflow: clip;
  position: sticky;
  left: 0;
  z-index: 3;
  background: #fafafa;
`;

const CheckboxTd = styled(Td)`
  width: 50px;
  min-width: 50px;
  max-width: 50px;
  padding: 0 8px;
  overflow: visible;
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  text-overflow: clip;
`;

// ---- Column Menu ----

const ColumnMenu = ({ column, table, storageKey }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const openMenu = e => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const closeMenu = () => setAnchorEl(null);

  const currentSort = column.getIsSorted();
  const isPinned = column.getIsPinned();

  const handleSortAsc = () => {
    column.toggleSorting(false);
    closeMenu();
  };
  const handleSortDesc = () => {
    column.toggleSorting(true);
    closeMenu();
  };
  const handleClearSort = () => {
    column.clearSorting();
    closeMenu();
  };

  const handlePinLeft = () => {
    column.pin('left');
    closeMenu();
  };
  const handlePinRight = () => {
    column.pin('right');
    closeMenu();
  };
  const handleUnpin = () => {
    column.pin(false);
    closeMenu();
  };

  const handleAutosizeThis = () => {
    column.resetSize();
    closeMenu();
  };
  const handleAutosizeAll = () => {
    table.resetColumnSizing();
    closeMenu();
  };

  const handleResetAll = () => {
    table.resetColumnSizing();
    table.resetSorting();
    table.resetColumnOrder();
    table.resetColumnPinning();
    if (storageKey) {
      try {
        localStorage.removeItem('table-state-' + storageKey);
      } catch (e) {
        // ignore
      }
    }
    closeMenu();
  };

  return (
    <>
      <MenuBtn size="small" onClick={openMenu}>
        <MoreVertIcon style={{ fontSize: 16 }} />
      </MenuBtn>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleSortAsc} selected={currentSort === 'asc'}>
          <ListItemIcon>
            <ArrowUpwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sort Ascending" />
        </MenuItem>
        <MenuItem onClick={handleSortDesc} selected={currentSort === 'desc'}>
          <ListItemIcon>
            <ArrowDownwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sort Descending" />
        </MenuItem>
        {currentSort && (
          <MenuItem onClick={handleClearSort}>
            <ListItemIcon>
              <span style={{ fontSize: 18, marginLeft: 4 }}>✕</span>
            </ListItemIcon>
            <ListItemText primary="Clear Sort" />
          </MenuItem>
        )}

        <Divider />

        {isPinned !== 'left' && (
          <MenuItem onClick={handlePinLeft}>
            <ListItemIcon>
              <span style={{ fontSize: 16 }}>◧</span>
            </ListItemIcon>
            <ListItemText primary="Pin Left" />
          </MenuItem>
        )}
        {isPinned !== 'right' && (
          <MenuItem onClick={handlePinRight}>
            <ListItemIcon>
              <span style={{ fontSize: 16 }}>◨</span>
            </ListItemIcon>
            <ListItemText primary="Pin Right" />
          </MenuItem>
        )}
        {isPinned && (
          <MenuItem onClick={handleUnpin}>
            <ListItemIcon>
              <span style={{ fontSize: 16 }}>☐</span>
            </ListItemIcon>
            <ListItemText primary="Unpin" />
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={handleAutosizeThis}>
          <ListItemIcon>
            <HeightIcon
              fontSize="small"
              style={{ transform: 'rotate(90deg)' }}
            />
          </ListItemIcon>
          <ListItemText primary="Reset This Column Width" />
        </MenuItem>
        <MenuItem onClick={handleAutosizeAll}>
          <ListItemIcon>
            <HeightIcon
              fontSize="small"
              style={{ transform: 'rotate(90deg)' }}
            />
          </ListItemIcon>
          <ListItemText primary="Reset All Column Widths" />
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleResetAll}>
          <ListItemIcon>
            <SettingsBackupRestoreIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Reset Everything" />
        </MenuItem>
      </Menu>
    </>
  );
};

ColumnMenu.propTypes = {
  column: shape({}).isRequired,
  table: shape({}).isRequired,
  storageKey: string,
};
ColumnMenu.defaultProps = { storageKey: null };

// ---- Column Visibility ----

const ColumnVisibilityButton = ({ table }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <IconButton
        size="small"
        onClick={e => setAnchorEl(e.currentTarget)}
        title="Toggle columns"
      >
        <ViewColumnIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {table.getAllLeafColumns().map(column => (
          <MenuItem
            key={column.id}
            onClick={() => column.toggleVisibility()}
            dense
          >
            <ListItemIcon style={{ minWidth: 32 }}>
              {column.getIsVisible() ? (
                <VisibilityIcon fontSize="small" />
              ) : (
                <VisibilityOffIcon fontSize="small" style={{ color: '#bbb' }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={column.columnDef.header}
              style={{ opacity: column.getIsVisible() ? 1 : 0.5 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

ColumnVisibilityButton.propTypes = {
  table: shape({}).isRequired,
};

// ---- Toolbar ----

const EnhancedTableToolbar = ({
  data,
  selected = [],
  setSelected,
  count,
  title,
  titlePlural,
  toolbarButtons: Buttons,
  columnVisibilityButton,
}) => (
  <StyledToolbar>
    <div className="title">
      {selected.length > 0 ? (
        <Typography variant="h6" id="tableTitleSelection">
          {selected.length} / {data.length} selected
        </Typography>
      ) : (
        <Typography variant="h6" id="tableTitle">
          {count} {count === 1 ? title : titlePlural}
        </Typography>
      )}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {columnVisibilityButton}
      {Buttons ? (
        <Buttons
          key="tb-button"
          selected={selected}
          setSelected={setSelected}
        />
      ) : null}
    </div>
  </StyledToolbar>
);

EnhancedTableToolbar.propTypes = {
  data: arrayOf(shape({})),
  selected: arrayOf(shape([])),
  setSelected: func.isRequired,
  count: number,
  title: string,
  titlePlural: string,
  toolbarButtons: oneOfType([node, func]),
};

EnhancedTableToolbar.defaultProps = {
  data: [],
  selected: [],
  count: null,
  title: '',
  titlePlural: '',
  toolbarButtons: undefined,
};

// ---- Persistence ----

const STORAGE_PREFIX = 'table-state-';

const saveState = (storageKey, state) => {
  if (!storageKey) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(state));
  } catch (e) {
    // ignore
  }
};

const loadState = storageKey => {
  if (!storageKey) return null;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// ---- Main Table ----

const MyTable = ({
  data,
  columns: columnsProp,
  scrollableDiv,
  onRowClick,
  hideToolbar,
  sort: initialSort,
  title,
  titlePlural,
  onSelectedChange,
  toolbarButtons,
  storageKey,
}) => {
  const containerRef = useRef(null);
  const [rowSelection, setRowSelection] = useState({});
  const [containerHeight, setContainerHeight] = useState(600);

  // Load persisted state
  const savedState = useMemo(() => loadState(storageKey), [storageKey]);

  const [sorting, setSorting] = useState(
    savedState?.sorting ||
      (initialSort ? [{ id: 'id', desc: initialSort === 'desc' }] : []),
  );
  const [columnSizing, setColumnSizing] = useState(
    savedState?.columnSizing || {},
  );
  const [columnOrder, setColumnOrder] = useState(savedState?.columnOrder || []);
  const [columnPinning, setColumnPinning] = useState(
    savedState?.columnPinning || { left: [], right: [] },
  );
  const [columnVisibility, setColumnVisibility] = useState(
    savedState?.columnVisibility || {},
  );

  const dragColumnRef = useRef(null);

  const handleDragStart = useCallback((e, columnId) => {
    dragColumnRef.current = columnId;
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.currentTarget.classList.add('drag-over');
  }, []);

  const handleDragLeave = useCallback(e => {
    e.currentTarget.classList.remove('drag-over');
  }, []);

  // Persist state changes
  useEffect(() => {
    saveState(storageKey, {
      sorting,
      columnSizing,
      columnOrder,
      columnPinning,
      columnVisibility,
    });
  }, [
    storageKey,
    sorting,
    columnSizing,
    columnOrder,
    columnPinning,
    columnVisibility,
  ]);

  // Container height from scrollableDiv
  useEffect(() => {
    if (scrollableDiv && scrollableDiv.current) {
      const el = scrollableDiv.current;
      const update = () => setContainerHeight(el.offsetHeight || 600);
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    return undefined;
  }, [scrollableDiv]);

  // Convert column format
  const tanstackColumns = useMemo(() => {
    return columnsProp.map(col => ({
      id: col.dataKey,
      accessorKey: col.dataKey,
      header: col.label,
      meta: { wrap: col.wrap },
      size: col.width || 150,
      minSize: col.minWidth || 50,
      maxSize: col.maxWidth || 800,
      enableSorting: !col.disableSort,
    }));
  }, [columnsProp]);

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    state: {
      sorting,
      columnSizing,
      columnOrder: columnOrder.length > 0 ? columnOrder : undefined,
      columnPinning,
      columnVisibility,
      rowSelection,
    },
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: hideToolbar ? undefined : setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    enableRowSelection: !hideToolbar,
    getRowId: row => String(row.id),
  });

  const handleDrop = useCallback(
    (e, targetColumnId) => {
      e.preventDefault();
      e.currentTarget.classList.remove('drag-over');
      const draggedId = dragColumnRef.current;
      if (!draggedId || draggedId === targetColumnId) return;

      const allCols = table.getAllLeafColumns().map(c => c.id);
      const currentOrder = columnOrder.length > 0 ? columnOrder : allCols;
      const fromIndex = currentOrder.indexOf(draggedId);
      const toIndex = currentOrder.indexOf(targetColumnId);
      if (fromIndex === -1 || toIndex === -1) return;

      const newOrder = [...currentOrder];
      newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, draggedId);
      setColumnOrder(newOrder);
      dragColumnRef.current = null;
    },
    [columnOrder, setColumnOrder, table],
  );

  // Bridge selection to toolbar
  const selectedRows = useMemo(() => {
    return table.getSelectedRowModel().rows.map(r => r.original);
  }, [table, rowSelection]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onSelectedChange) {
      onSelectedChange(selectedRows);
    }
  }, [selectedRows, onSelectedChange]);

  const setSelectedForToolbar = useCallback(
    newSelected => {
      if (typeof newSelected === 'function') {
        const result = newSelected(selectedRows);
        const newSel = {};
        if (result && result.length > 0) {
          result.forEach(r => {
            newSel[String(r.id)] = true;
          });
        }
        setRowSelection(newSel);
      } else {
        const newSel = {};
        if (newSelected && newSelected.length > 0) {
          newSelected.forEach(r => {
            newSel[String(r.id)] = true;
          });
        }
        setRowSelection(newSel);
      }
    },
    [selectedRows],
  );

  // Virtual scrolling
  const { rows } = table.getRowModel();

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 56,
    overscan: 10,
  });

  const virtualRows = virtualizer.getVirtualItems();

  const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? virtualizer.getTotalSize() -
        (virtualRows[virtualRows.length - 1]?.end || 0)
      : 0;

  // Header groups
  const headerGroups = table.getHeaderGroups();

  // Compute pinned offsets for sticky positioning
  const getPinnedOffset = (header, side) => {
    const headers = headerGroups[0]?.headers || [];
    let offset = 0;
    if (side === 'left') {
      if (!hideToolbar) offset = 50; // checkbox column width
      for (const h of headers) {
        if (h.id === header.id) break;
        if (h.column.getIsPinned() === 'left') {
          offset += h.getSize();
        }
      }
    }
    if (side === 'right') {
      const reversed = [...headers].reverse();
      for (const h of reversed) {
        if (h.id === header.id) break;
        if (h.column.getIsPinned() === 'right') {
          offset += h.getSize();
        }
      }
    }
    return offset;
  };

  const isAllSelected = table.getIsAllRowsSelected();
  const isSomeSelected = table.getIsSomeRowsSelected();

  return (
    <>
      <EnhancedTableToolbar
        data={data}
        selected={selectedRows}
        setSelected={setSelectedForToolbar}
        title={title}
        count={data.length}
        titlePlural={titlePlural}
        toolbarButtons={toolbarButtons}
        columnVisibilityButton={<ColumnVisibilityButton table={table} />}
      />
      <TableContainer ref={containerRef} height={containerHeight}>
        <StyledTable
          style={{
            minWidth: table.getTotalSize() + (!hideToolbar ? 50 : 0),
            width: '100%',
          }}
        >
          <Thead>
            {headerGroups.map(hg => (
              <tr key={hg.id}>
                {!hideToolbar && (
                  <CheckboxTh>
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isSomeSelected}
                      onChange={table.getToggleAllRowsSelectedHandler()}
                      size="small"
                    />
                  </CheckboxTh>
                )}
                {hg.headers.map(header => {
                  const pinned = header.column.getIsPinned();
                  return (
                    <Th
                      key={header.id}
                      style={{ width: header.getSize() }}
                      pinned={pinned || undefined}
                      pinnedOffset={
                        pinned ? getPinnedOffset(header, pinned) : undefined
                      }
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={e => handleDrop(e, header.column.id)}
                    >
                      <ThContent>
                        <DragHandle
                          draggable
                          onDragStart={e =>
                            handleDragStart(e, header.column.id)
                          }
                        >
                          <DragIndicatorIcon style={{ fontSize: 14 }} />
                        </DragHandle>
                        <ThLabel
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <ThLabelText>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </ThLabelText>
                          {header.column.getCanSort() && (
                            <SortIcon active={!!header.column.getIsSorted()}>
                              {header.column.getIsSorted() === 'asc' ? (
                                <ArrowUpwardIcon style={{ fontSize: 14 }} />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ArrowDownwardIcon style={{ fontSize: 14 }} />
                              ) : (
                                <UnfoldMoreIcon style={{ fontSize: 16 }} />
                              )}
                            </SortIcon>
                          )}
                        </ThLabel>
                        <ColumnMenu
                          column={header.column}
                          table={table}
                          storageKey={storageKey}
                        />
                      </ThContent>
                      <ResizeHandle
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        isResizing={header.column.getIsResizing()}
                      />
                    </Th>
                  );
                })}
              </tr>
            ))}
          </Thead>
          <tbody>
            {paddingTop > 0 && (
              <tr>
                <td
                  style={{
                    height: `${paddingTop}px`,
                    padding: 0,
                    border: 'none',
                  }}
                />
              </tr>
            )}
            {virtualRows.map(virtualRow => {
              const row = rows[virtualRow.index];
              const isSelected = row.getIsSelected();
              return (
                <Tr
                  key={row.id}
                  clickable={!!onRowClick}
                  selected={isSelected}
                  onClick={() =>
                    onRowClick && onRowClick({ rowData: row.original })
                  }
                >
                  {!hideToolbar && (
                    <CheckboxTd>
                      <Checkbox
                        checked={isSelected}
                        onChange={row.getToggleSelectedHandler()}
                        onClick={e => e.stopPropagation()}
                        size="small"
                      />
                    </CheckboxTd>
                  )}
                  {row.getVisibleCells().map(cell => {
                    const pinned = cell.column.getIsPinned();
                    const header = headerGroups[0]?.headers.find(
                      h => h.id === cell.column.id,
                    );
                    return (
                      <Td
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                        pinned={pinned || undefined}
                        pinnedOffset={
                          pinned && header
                            ? getPinnedOffset(header, pinned)
                            : undefined
                        }
                      >
                        {cell.column.columnDef.meta?.wrap ? (
                          <CellClamp>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </CellClamp>
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )
                        )}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr>
                <td
                  style={{
                    height: `${paddingBottom}px`,
                    padding: 0,
                    border: 'none',
                  }}
                />
              </tr>
            )}
          </tbody>
        </StyledTable>
      </TableContainer>
    </>
  );
};

MyTable.propTypes = {
  onRowClick: func.isRequired,
  data: arrayOf(shape({})),
  columns: arrayOf(shape()).isRequired,
  scrollableDiv: shape().isRequired,
  sort: string,
  hideToolbar: bool,
  title: string,
  titlePlural: string,
  onSelectedChange: func,
  toolbarButtons: oneOfType([node, func]),
  storageKey: string,
};

MyTable.defaultProps = {
  sort: 'desc',
  data: [],
  hideToolbar: false,
  title: 'Row',
  titlePlural: 'Rows',
  onSelectedChange: undefined,
  toolbarButtons: undefined,
  storageKey: null,
};

export default MyTable;
